import { useState, useEffect, useRef, useCallback } from "react";
import "./FleetMessagePrompt.css";
import {
  serviceGetOrganizationDetails,
  serviceMessageSend,
} from "../../api/serviceFleetMaster";
import { toast } from "react-toastify";

const MESSAGE_TYPES = [
  {
    value: "alert",
    label: "ALERT",
    icon: "bi-exclamation-triangle-fill",
    color: "#ef4444",
  },
  {
    value: "command",
    label: "CMD",
    icon: "bi-terminal-fill",
    // color: "#8b5cf6",
    color: "#3B82F6",
  },
  {
    value: "request",
    label: "REQ",
    icon: "bi-question-circle-fill",
    color: "#f59e0b",
  },
  {
    value: "info",
    label: "INFO",
    icon: "bi-info-circle-fill",
    color: "#2ca330ff",
  },
];

const FleetMessagePrompt = ({ fleet, onClose, onSent }) => {
  const [organizationId, setOrganizationId] = useState(null);

  const fetchOrganization = useCallback(async () => {
    try {
      const data = await serviceGetOrganizationDetails();

      setOrganizationId(data._id);
    } catch (err) {
      console.error("Failed to fetch admin profile:", err);
      toast.error(`Error: ${err?.message ?? "Unknown to load profile"}`);
      setOrganizationId(null);
    }
  }, []);

  useEffect(() => {
    fetchOrganization();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchOrganization]);

  const MESSAGE_MAX_TITLE = 32;
  const MESSAGE_MAX_DETAILS = 128;

  const [type, setType] = useState("info");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const titleRef = useRef(null);

  const selectedType = MESSAGE_TYPES.find((t) => t.value === type);

  useEffect(() => {
    setTimeout(() => titleRef.current?.focus(), 120);
  }, []);

  const handleSend = async () => {
    if (!title.trim()) return setError("Title is required.");
    if (!body.trim()) return setError("Message body is required.");

    setSending(true);
    setError(null);

    try {
      //build payload for message
      const payload = {
        organizationId,
        vehicleId: fleet?.vehicleId,
        messageType: type,
        messageTitle: title.trim(),
        messageDetails: body.trim(),
      };

      const data = await serviceMessageSend(organizationId, fleet?.vehicleId, payload);
      toast.success("Message sent!");
      // onSent?.(data);
      onSent({
        _id: data.messageId,
        messageCode: data.messageCode,
        vehicleId: fleet.vehicleId,
        vehicleCode: fleet.vehicleCode,
        messageType: type, // from your local state
        messageTitle: title.trim(), // from your local state
        messageDetails: body.trim(), // from your local state
        messageStatus: "sent",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      onClose?.();
    } catch (err) {
      setError(err.message || "Failed to send.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fmp-backdrop"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="fmp-sheet">
        {/* Header */}
        <div className="fmp-header">
          <span className="fmp-to-label">Message:</span>
          <span className="fmp-to-rego">
            {fleet?.name || fleet?.vehicleCode || "Vehicle"}
          </span>

          {error && (
            <div className="fmp-error">
              <i className="bi bi-exclamation-circle" /> {error}
            </div>
          )}
        </div>

        {/* Type Selector — Forced onto one tight row */}
        <div className="fmp-type-row">
          {MESSAGE_TYPES.map((t) => (
            <button
              key={t.value}
              className={`fmp-type-chip ${type === t.value ? "fmp-type-chip--active" : ""}`}
              style={{ "--chip-color": t.color }}
              onClick={() => setType(t.value)}
            >
              <i className={`bi ${t.icon}`} />
              {t.label}
            </button>
          ))}
        </div>

        {/* Inputs Form */}
        <div className="fmp-form-grid">
          {/* Title */}
          <div className="fmp-field">
            <div className="fmp-label-row">
              <label className="fmp-label">Subject</label>
              <span className="fmp-char-count">
                {title.length}/{MESSAGE_MAX_TITLE}
              </span>
            </div>
            <input
              ref={titleRef}
              className="fmp-input"
              type="text"
              placeholder="Enter Message title..."
              maxLength={MESSAGE_MAX_TITLE}
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setError(null);
              }}
            />
          </div>

          {/* Body */}
          <div className="fmp-field">
            <div className="fmp-label-row">
              <label className="fmp-label">Message</label>
              <span className="fmp-char-count">
                {body.length}/{MESSAGE_MAX_DETAILS}
              </span>
            </div>
            <textarea
              className="fmp-textarea"
              placeholder="Instructions for driver..."
              maxLength={MESSAGE_MAX_DETAILS}
              rows={3}
              value={body}
              onChange={(e) => {
                setBody(e.target.value);
                setError(null);
              }}
            />
          </div>
        </div>

        {/* Dynamic Action Row */}
        <div className="fmp-action-controls">
          <button className="fmp-close" onClick={onClose} type="button">
            CANCEL
          </button>
          <button
            className="fmp-send-btn"
            style={{ "--chip-color": selectedType.color }}
            onClick={handleSend}
            disabled={sending}
          >
            {sending ? (
              <i className="bi bi-arrow-repeat fmp-spin" />
            ) : (
              <>
                <i className={`bi ${selectedType.icon}`} /> SEND
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FleetMessagePrompt;
